// src/pages/founder/SystemHealth.jsx
// Level 2: System Health Detail Page - Light Theme

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Activity,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Server,
  Mail,
  MessageSquare,
  Zap,
  RefreshCw,
  ChevronRight,
  TrendingUp,
  TrendingDown
} from "lucide-react";
import { getPilotSystemHealth, getPilotSystemErrors } from "../../api";

const PERIOD_OPTIONS = [
  { value: '1_hour', label: 'Last Hour' },
  { value: '24_hours', label: 'Last 24 Hours' },
  { value: '7_days', label: 'Last 7 Days' }
];

export default function SystemHealth() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState('24_hours');
  const [data, setData] = useState(null);
  const [errors, setErrors] = useState(null);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const [healthRes, errorsRes] = await Promise.all([
        getPilotSystemHealth({ period }),
        getPilotSystemErrors({ limit: 20 })
      ]);
      setData(healthRes?.data);
      setErrors(errorsRes?.data);
    } catch (err) {
      console.error('Failed to load system health:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational': return 'text-emerald-600';
      case 'degraded': return 'text-amber-600';
      case 'down': return 'text-red-600';
      default: return 'text-slate-600';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'operational': return 'bg-emerald-500';
      case 'degraded': return 'bg-amber-500';
      case 'down': return 'bg-red-500';
      default: return 'bg-slate-500';
    }
  };

  const getStatusBanner = (status) => {
    switch (status) {
      case 'operational': return 'bg-emerald-50 border-emerald-200';
      case 'degraded': return 'bg-amber-50 border-amber-200';
      case 'down': return 'bg-red-50 border-red-200';
      default: return 'bg-slate-50 border-slate-200';
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/founder')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Command Center
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
                <Activity className="w-6 h-6 text-cyan-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">System Health</h1>
                <p className="text-slate-500">Monitor API performance and delivery metrics</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="bg-white border border-slate-300 text-slate-700 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              >
                {PERIOD_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <button
                onClick={() => loadData(true)}
                disabled={refreshing}
                className="p-2 bg-white hover:bg-slate-50 border border-slate-300 rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 text-slate-600 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Overall Status Banner */}
        <div className={`rounded-2xl p-6 border ${getStatusBanner(data?.status)}`}>
          <div className="flex items-center gap-4">
            <div className={`w-4 h-4 rounded-full ${getStatusBg(data?.status)} animate-pulse`}></div>
            <div>
              <h2 className={`text-xl font-bold ${getStatusColor(data?.status)}`}>
                {data?.status === 'operational' ? 'All Systems Operational' :
                 data?.status === 'degraded' ? 'Some Systems Degraded' :
                 'System Issues Detected'}
              </h2>
              <p className="text-slate-500">
                Last checked: {new Date().toLocaleTimeString()}
              </p>
            </div>
          </div>
        </div>

        {/* Service Status Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* API Status */}
          <ServiceCard
            icon={<Server className="w-6 h-6" />}
            name="API Server"
            status="operational"
            metrics={[
              { label: 'Uptime', value: `${data?.api?.uptime || 99.9}%` },
              { label: 'Avg Response', value: `${data?.api?.avgResponseTime || 0}ms` },
              { label: 'Requests', value: data?.api?.totalRequests?.toLocaleString() || 0 },
              { label: 'Errors', value: data?.api?.totalErrors || 0 }
            ]}
          />

          {/* SMS Status */}
          <ServiceCard
            icon={<MessageSquare className="w-6 h-6" />}
            name="SMS (Twilio)"
            status={data?.sms?.deliveryRate >= 90 ? 'operational' : data?.sms?.deliveryRate >= 70 ? 'degraded' : 'down'}
            metrics={[
              { label: 'Sent', value: data?.sms?.sent || 0 },
              { label: 'Delivered', value: data?.sms?.delivered || 0 },
              { label: 'Failed', value: data?.sms?.failed || 0 },
              { label: 'Delivery Rate', value: `${data?.sms?.deliveryRate || 0}%` }
            ]}
          />

          {/* Email Status */}
          <ServiceCard
            icon={<Mail className="w-6 h-6" />}
            name="Email (SendGrid)"
            status={data?.email?.deliveryRate >= 90 ? 'operational' : data?.email?.deliveryRate >= 70 ? 'degraded' : 'down'}
            metrics={[
              { label: 'Sent', value: data?.email?.sent || 0 },
              { label: 'Delivered', value: data?.email?.delivered || 0 },
              { label: 'Failed', value: data?.email?.failed || 0 },
              { label: 'Delivery Rate', value: `${data?.email?.deliveryRate || 0}%` }
            ]}
          />

          {/* Database Status */}
          <ServiceCard
            icon={<Zap className="w-6 h-6" />}
            name="Database"
            status="operational"
            metrics={[
              { label: 'Connections', value: 'Active' },
              { label: 'Pool Size', value: '20' },
              { label: 'Slow Queries', value: '0' },
              { label: 'Status', value: 'Healthy' }
            ]}
          />
        </div>

        {/* Detailed Metrics */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* API Performance */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <div className="w-10 h-10 bg-cyan-100 rounded-xl flex items-center justify-center">
                <Server className="w-5 h-5 text-cyan-600" />
              </div>
              API Performance
            </h3>
            <div className="space-y-4">
              <MetricRow
                label="Average Response Time"
                value={`${data?.api?.avgResponseTime || 0}ms`}
                target="<500ms"
                isGood={(data?.api?.avgResponseTime || 0) < 500}
              />
              <MetricRow
                label="Error Rate"
                value={`${data?.api?.errorRate || 0}%`}
                target="<1%"
                isGood={(data?.api?.errorRate || 0) < 1}
              />
              <MetricRow
                label="Total Requests"
                value={data?.api?.totalRequests?.toLocaleString() || 0}
                target="—"
              />
              <MetricRow
                label="Total Errors"
                value={data?.api?.totalErrors || 0}
                target="0"
                isGood={(data?.api?.totalErrors || 0) === 0}
              />
            </div>
          </div>

          {/* Delivery Metrics */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              Delivery Metrics
            </h3>
            <div className="space-y-4">
              <MetricRow
                label="SMS Delivery Rate"
                value={`${data?.sms?.deliveryRate || 0}%`}
                target=">95%"
                isGood={(data?.sms?.deliveryRate || 0) >= 95}
              />
              <MetricRow
                label="Email Delivery Rate"
                value={`${data?.email?.deliveryRate || 0}%`}
                target=">95%"
                isGood={(data?.email?.deliveryRate || 0) >= 95}
              />
              <MetricRow
                label="SMS Failed"
                value={data?.sms?.failed || 0}
                target="0"
                isGood={(data?.sms?.failed || 0) === 0}
              />
              <MetricRow
                label="Email Failed"
                value={data?.email?.failed || 0}
                target="0"
                isGood={(data?.email?.failed || 0) === 0}
              />
            </div>
          </div>
        </div>

        {/* Error Log */}
        {(data?.errors?.total > 0 || errors?.errors?.length > 0) && (
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div
              className="p-4 border-b border-slate-200 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
              onClick={() => setShowErrors(!showErrors)}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">Recent Errors</h3>
                <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                  {data?.errors?.total || errors?.errors?.length || 0}
                </span>
              </div>
              <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${showErrors ? 'rotate-90' : ''}`} />
            </div>
            {showErrors && (
              <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                {errors?.errors?.map((error, idx) => (
                  <div key={idx} className="p-4 hover:bg-slate-50 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="text-slate-900 font-medium">{error.action}</div>
                        <div className="text-sm text-slate-500">{error.resource_type}</div>
                        {error.details && (
                          <pre className="mt-2 text-xs text-slate-600 bg-slate-100 rounded p-2 overflow-x-auto border border-slate-200">
                            {JSON.stringify(error.details, null, 2)}
                          </pre>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 whitespace-nowrap">
                        {new Date(error.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
                {(!errors?.errors || errors.errors.length === 0) && (
                  <div className="p-8 text-center text-slate-500">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No recent errors</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Recommendations */}
        {(data?.sms?.deliveryRate < 95 || data?.email?.deliveryRate < 95 || data?.api?.errorRate > 1) && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Recommendations</h3>
                <ul className="space-y-2 text-slate-700">
                  {data?.sms?.deliveryRate < 95 && (
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                      SMS delivery rate is below 95% - check Twilio dashboard for issues
                    </li>
                  )}
                  {data?.email?.deliveryRate < 95 && (
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                      Email delivery rate is below 95% - check SendGrid for bounces/complaints
                    </li>
                  )}
                  {data?.api?.errorRate > 1 && (
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                      API error rate is above 1% - review error logs for patterns
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* All Good State */}
        {(data?.sms?.deliveryRate == null || data.sms.deliveryRate >= 95) &&
         (data?.email?.deliveryRate == null || data.email.deliveryRate >= 95) &&
         (data?.api?.errorRate == null || data.api.errorRate <= 1) && (
          <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center shrink-0">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">All Systems Healthy</h3>
                <p className="text-slate-600">
                  All delivery rates are above target and error rates are within acceptable limits.
                </p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function ServiceCard({ icon, name, status, metrics }) {
  const statusColors = {
    operational: { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Operational' },
    degraded: { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500', label: 'Degraded' },
    down: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500', label: 'Down' }
  };

  const s = statusColors[status] || statusColors.operational;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center text-slate-600">
            {icon}
          </div>
          <span className="text-slate-900 font-semibold">{name}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${s.dot}`}></div>
          <span className={`text-xs font-medium ${s.text}`}>{s.label}</span>
        </div>
      </div>
      <div className="space-y-2">
        {metrics.map((metric, idx) => (
          <div key={idx} className="flex items-center justify-between text-sm">
            <span className="text-slate-500">{metric.label}</span>
            <span className="text-slate-900 font-medium">{metric.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MetricRow({ label, value, target, isGood }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
      <span className="text-slate-600">{label}</span>
      <div className="flex items-center gap-3">
        <span className="text-slate-900 font-semibold">{value}</span>
        {target !== '—' && isGood !== undefined && (
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
            isGood ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
          }`}>
            {isGood ? <CheckCircle2 className="w-3 h-3 inline" /> : <XCircle className="w-3 h-3 inline" />}
          </span>
        )}
        <span className="text-xs text-slate-500">{target}</span>
      </div>
    </div>
  );
}
